import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Heart, CreditCard, Shield, Gift, Users, Target, Award } from 'lucide-react';

const Donate = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [isProcessing, setIsProcessing] = useState(false);
  const [donationSuccess, setDonationSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const presetAmounts = [25, 50, 100, 250, 500, 1000];
  const customAmount = selectedAmount === 'custom' ? '' : selectedAmount;

  const donationCategories = [
    {
      id: 'general',
      title: 'General Fund',
      description: 'Support our overall mission and operational costs',
      icon: Heart,
      color: 'text-green-500'
    },
    {
      id: 'blood-drive',
      title: 'Blood Drive Programs',
      description: 'Help organize and expand blood donation camps',
      icon: Users,
      color: 'text-orange-500'
    },
    {
      id: 'health-camp',
      title: 'Health Camps',
      description: 'Support community health awareness programs',
      icon: Target,
      color: 'text-green-600'
    },
    {
      id: 'awareness',
      title: 'Health Awareness',
      description: 'Fund educational programs and workshops',
      icon: Award,
      color: 'text-green-700'
    }
  ];

  const impactExamples = [
    { amount: 25, impact: 'Provides blood testing supplies for 5 donors' },
    { amount: 50, impact: 'Supports one blood donation camp participant' },
    { amount: 100, impact: 'Funds emergency response training for 2 volunteers' },
    { amount: 250, impact: 'Organizes a small community health workshop' },
    { amount: 500, impact: 'Supports a blood drive for 50+ participants' },
    { amount: 1000, impact: 'Funds a major health awareness campaign' }
  ];

  const onSubmit = async (data) => {
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      setDonationSuccess(true);
      reset();
      setIsProcessing(false);
      setTimeout(() => setDonationSuccess(false), 10000);
    }, 2000);
  };

  const getImpactExample = (amount) => {
    return impactExamples.find(example => example.amount <= amount) || impactExamples[0];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/aim-logo.png" 
              alt="AIM Foundation Logo" 
              className="w-20 h-20 md:w-24 md:h-24 object-contain"
            />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Make a Difference
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Your donation helps us save lives and build healthier communities
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Donation Form */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div className="flex items-center mb-6">
                <img 
                  src="/aim-logo.png" 
                  alt="AIM Foundation Logo" 
                  className="w-10 h-10 object-contain"
                />
                <h2 className="ml-3 text-2xl font-bold text-gray-900 dark:text-white">
                  Donate Now
                </h2>
              </div>

              {donationSuccess && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
                  <div className="flex items-center">
                    <Heart className="w-5 h-5 mr-2" />
                    <span className="font-medium">Thank you for your donation!</span>
                  </div>
                  <p className="mt-1 text-sm">
                    Your contribution will help us continue our mission of empowering communities through comprehensive health programs and social support initiatives.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Donation Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Where would you like your donation to go? *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {donationCategories.map((category) => (
                      <label key={category.id} className="relative flex cursor-pointer rounded-lg border border-gray-300 p-4 hover:border-red-500 focus:outline-none">
                        <input
                          type="radio"
                          value={category.id}
                          className="sr-only"
                          {...register('category', { required: 'Please select a category' })}
                        />
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center">
                            <div className="text-sm">
                              <div className="flex items-center">
                                <category.icon className={`w-5 h-5 ${category.color} mr-2`} />
                                <span className="font-medium text-gray-900 dark:text-white">{category.title}</span>
                              </div>
                              <p className="text-gray-500 dark:text-gray-400 text-xs">{category.description}</p>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.category.message}</p>
                  )}
                </div>

                {/* Donation Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Select Donation Amount *
                  </label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                    {presetAmounts.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => setSelectedAmount(amount)}
                        className={`py-3 px-4 rounded-md font-medium transition-colors duration-200 ${
                          selectedAmount === amount
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      placeholder="Enter custom amount"
                      value={customAmount}
                      onChange={(e) => setSelectedAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500"
                      min="1"
                    />
                  </div>
                </div>

                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name *
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        errors.firstName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      {...register('firstName', { required: 'First name is required' })}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name *
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        errors.lastName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      {...register('lastName', { required: 'Last name is required' })}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    type="email"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    rows={3}
                    placeholder="Share why you're donating or leave a message..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    {...register('message')}
                  />
                </div>

                {/* Anonymous Donation */}
                <div className="flex items-center">
                  <input
                    id="anonymous"
                    type="checkbox"
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    {...register('anonymous')}
                  />
                  <label htmlFor="anonymous" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Make this donation anonymous
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessing || !selectedAmount}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-md font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isProcessing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Donate ${selectedAmount}
                    </>
                  )}
                </button>

                {/* Security Notice */}
                <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  <Shield className="w-4 h-4 mr-2" />
                  Your donation is secure and encrypted
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Impact Calculator */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Your Impact
              </h3>
              {selectedAmount && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600 mb-2">
                    ${selectedAmount}
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {getImpactExample(selectedAmount).impact}
                  </p>
                </div>
              )}
            </div>

            {/* Why Donate */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Why Your Donation Matters
              </h3>
              <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <Heart className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Every $1 helps us reach more potential donors</span>
                </li>
                <li className="flex items-start">
                  <Users className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Funds community health programs and workshops</span>
                </li>
                <li className="flex items-start">
                  <Target className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Supports emergency response training</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-4 h-4 text-green-700 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Helps organize blood donation camps</span>
                </li>
              </ul>
            </div>

            {/* Tax Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Tax Deductible
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                AIM Foundation is a registered 501(c)(3) non-profit organization. Your donation is tax-deductible to the extent allowed by law.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                You'll receive a receipt via email for your records.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate;
